import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import EditOutlined from '@mui/icons-material/EditOutlined';
import RestoreOutlined from '@mui/icons-material/RestoreOutlined';
import Add from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  getDashboardErrorMessage,
  useArchiveDashboardMetricMutation,
  useCreateDashboardMetricMutation,
  useReorderDashboardMetricsMutation,
  useRestoreDashboardMetricMutation,
  useUpdateDashboardMetricMutation
} from './dashboardApi';
import DashboardFieldSourceSelector from './DashboardFieldSourceSelector';
import DashboardColourPicker from './DashboardColourPicker';
import {
  DASHBOARD_AGGREGATION,
  DASHBOARD_APPROVAL_MODE,
  DASHBOARD_METRIC_STATUS,
  DASHBOARD_VALUE_TYPE
} from './dashboardContract';
import {
  changeDashboardMetricValueType,
  createDashboardMetricKey,
  dashboardMetricToForm,
  DASHBOARD_AGGREGATION_OPTIONS,
  DASHBOARD_APPROVAL_OPTIONS,
  DASHBOARD_VALUE_TYPE_OPTIONS,
  EMPTY_DASHBOARD_METRIC_FORM,
  moveDashboardMetric,
  sortDashboardMetrics,
  validateDashboardMetric
} from './dashboardMetricModel';

const valueTypeLabel = (value) =>
  DASHBOARD_VALUE_TYPE_OPTIONS.find((option) => option.value === value)
    ?.label ?? value;

const aggregationLabel = (value) =>
  DASHBOARD_AGGREGATION_OPTIONS.find((option) => option.value === value)
    ?.label ?? value;

const MetricFields = ({
  errors,
  form,
  onChange,
  onLabelChange,
  onMetricKeyChange,
  onValueTypeChange
}) => (
  <Stack spacing={2.25}>
    <TextField
      autoFocus
      error={Boolean(errors.label)}
      fullWidth
      helperText={errors.label || `${form.label.length}/100 characters`}
      inputProps={{ maxLength: 101 }}
      label="Metric label"
      onChange={(event) => onLabelChange(event.target.value)}
      required
      value={form.label}
    />
    <TextField
      error={Boolean(errors.metricKey)}
      fullWidth
      helperText={
        errors.metricKey ||
        'Stable identifier used by imports and integrations. It cannot change after data is recorded.'
      }
      inputProps={{ maxLength: 64 }}
      label="Metric key"
      onChange={(event) => onMetricKeyChange(event.target.value)}
      required
      value={form.metricKey}
    />
    <TextField
      error={Boolean(errors.description)}
      fullWidth
      helperText={
        errors.description || `${form.description.length}/500 characters`
      }
      inputProps={{ maxLength: 501 }}
      label="Description"
      minRows={2}
      multiline
      onChange={(event) => onChange('description', event.target.value)}
      value={form.description}
    />

    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }
      }}
    >
      {!form.fieldSource && (
      <TextField
        error={Boolean(errors.valueType)}
        helperText={errors.valueType}
        disabled={!!form.fieldSource}
        label="Value type"
        onChange={(event) => onValueTypeChange(event.target.value)}
        required
        select
        value={form.valueType}
      >
        {DASHBOARD_VALUE_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      )}
      {!form.fieldSource && (
      <TextField
        disabled={!!form.fieldSource || form.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE}
        error={Boolean(errors.unitLabel)}
        helperText={errors.unitLabel || 'Optional, for example hours or items.'}
        inputProps={{ maxLength: 31 }}
        label="Unit label"
        onChange={(event) => onChange('unitLabel', event.target.value)}
        value={form.unitLabel}
      />
      )}
      {!form.fieldSource && (
      <TextField
        disabled={!!form.fieldSource || form.valueType === DASHBOARD_VALUE_TYPE.INTEGER}
        error={Boolean(errors.decimalPlaces)}
        helperText={errors.decimalPlaces || 'From 0 to 6.'}
        inputProps={{ max: 6, min: 0, step: 1 }}
        label="Decimal places"
        onChange={(event) => onChange('decimalPlaces', event.target.value)}
        type="number"
        value={form.decimalPlaces}
      />
      )}
      <TextField
        error={Boolean(errors.aggregation)}
        helperText={
          errors.aggregation || 'How values are combined across a date range.'
        }
        label="Aggregation"
        onChange={(event) => onChange('aggregation', event.target.value)}
        required
        select
        value={form.aggregation}
      >
        {DASHBOARD_AGGREGATION_OPTIONS.map((option) => (
          <MenuItem
            disabled={
              option.value === DASHBOARD_AGGREGATION.SUM &&
              form.valueType === DASHBOARD_VALUE_TYPE.PERCENTAGE
            }
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      {!form.fieldSource && (
      <TextField
        error={Boolean(errors.minimum)}
        helperText={errors.minimum || 'Optional.'}
        disabled={!!form.fieldSource}
        label="Minimum value"
        onChange={(event) => onChange('minimum', event.target.value)}
        type="number"
        value={form.minimum}
      />
      )}
      {!form.fieldSource && (
      <TextField
        error={Boolean(errors.maximum)}
        helperText={errors.maximum || 'Optional.'}
        disabled={!!form.fieldSource}
        label="Maximum value"
        onChange={(event) => onChange('maximum', event.target.value)}
        type="number"
        value={form.maximum}
      />
      )}
      {!form.fieldSource && (
        <TextField
          error={Boolean(errors.approvalMode)}
          helperText={
            errors.approvalMode ||
            'Controls whether submitted values need review.'
          }
          label="Approval policy"
          onChange={(event) => onChange('approvalMode', event.target.value)}
          required
          select
          value={form.approvalMode}
        >
          {DASHBOARD_APPROVAL_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      <DashboardColourPicker
        error={Boolean(errors.colour)}
        helperText={errors.colour || 'Optional six-digit hex colour.'}
        label="Display colour"
        onChange={(value) => onChange('colour', value)}
        value={form.colour}
      />
    </Box>

    <FormControlLabel
      control={
        <Checkbox
          disabled={!!form.fieldSource}
          checked={form.isRequired}
          onChange={(event) => onChange('isRequired', event.target.checked)}
        />
      }
      label="Require a value in every submission"
    />
  </Stack>
);

const MetricRow = ({
  canEdit,
  disableMoveDown,
  disableMoveUp,
  isBusy,
  onArchive,
  onEdit,
  onMoveDown,
  onMoveUp,
  onRestore,
  metric
}) => {
  const isArchived =
    metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ARCHIVED;

  return (
    <Paper
      data-testid={`dashboard-metric-definition-${metric.metricDefinitionId}`}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        opacity: isArchived ? 0.72 : 1,
        p: 2
      }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', md: 'center' }}
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack alignItems="flex-start" direction="row" spacing={1.5}>
          <Box
            aria-hidden="true"
            sx={{
              bgcolor: metric.colour || '#6D7785',
              borderRadius: '50%',
              flex: '0 0 auto',
              height: 14,
              mt: 0.75,
              width: 14
            }}
          />
          <Box>
            <Typography fontWeight={700}>{metric.label}</Typography>
            <Typography color="text.secondary" variant="caption">
              {metric.metricKey}
            </Typography>
            {metric.description && (
              <Typography
                color="text.secondary"
                sx={{ mt: 0.5 }}
                variant="body2"
              >
                {metric.description}
              </Typography>
            )}
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
              <Chip label={valueTypeLabel(metric.valueType)} size="small" />
              <Chip
                label={aggregationLabel(metric.aggregation)}
                size="small"
                variant="outlined"
              />
              <Chip
                color={
                  metric.approvalMode === DASHBOARD_APPROVAL_MODE.REQUIRED
                    ? 'warning'
                    : 'default'
                }
                label={
                  metric.fieldSource
                    ? 'Linked layout field'
                    : metric.approvalMode === DASHBOARD_APPROVAL_MODE.REQUIRED
                      ? 'Approval required'
                      : 'No approval'
                }
                size="small"
                variant="outlined"
              />
              {metric.isRequired && (
                <Chip label="Required entry" size="small" variant="outlined" />
              )}
              {isArchived && (
                <Chip color="default" label="Archived" size="small" />
              )}
            </Stack>
          </Box>
        </Stack>

        {canEdit && (
          <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.5}>
            <Tooltip title="Move up">
              <span>
                <IconButton
                  aria-label={`Move ${metric.label} up`}
                  disabled={disableMoveUp || isBusy}
                  onClick={onMoveUp}
                  size="small"
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton
                  aria-label={`Move ${metric.label} down`}
                  disabled={disableMoveDown || isBusy}
                  onClick={onMoveDown}
                  size="small"
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              disabled={isBusy}
              onClick={onEdit}
              size="small"
              startIcon={<EditOutlined />}
            >
              Edit
            </Button>
            {isArchived ? (
              <Button
                disabled={isBusy}
                onClick={onRestore}
                size="small"
                startIcon={<RestoreOutlined />}
              >
                Restore
              </Button>
            ) : (
              <Button
                color="warning"
                disabled={isBusy}
                onClick={onArchive}
                size="small"
                startIcon={<ArchiveOutlined />}
              >
                Archive
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const DashboardMetricDesigner = ({
  canEdit,
  dashboardTemplateId,
  draftVersion
}) => {
  const metrics = sortDashboardMetrics(draftVersion?.metrics ?? []);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [archiveMetric, setArchiveMetric] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_DASHBOARD_METRIC_FORM });
  const [errors, setErrors] = useState({});
  const [metricKeyTouched, setMetricKeyTouched] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [createMetric, createState] = useCreateDashboardMetricMutation();
  const [updateMetric, updateState] = useUpdateDashboardMetricMutation();
  const [archiveDashboardMetric, archiveState] =
    useArchiveDashboardMetricMutation();
  const [restoreDashboardMetric, restoreState] =
    useRestoreDashboardMetricMutation();
  const [reorderMetrics, reorderState] = useReorderDashboardMetricsMutation();
  const isBusy =
    createState.isLoading ||
    updateState.isLoading ||
    archiveState.isLoading ||
    restoreState.isLoading ||
    reorderState.isLoading;
  const actionError =
    archiveState.error || restoreState.error || reorderState.error;

  const clearActionStates = () => {
    archiveState.reset?.();
    restoreState.reset?.();
    reorderState.reset?.();
    setSuccessMessage(null);
  };

  const openCreate = () => {
    createState.reset?.();
    updateState.reset?.();
    setSelectedMetric(null);
    setForm({ ...EMPTY_DASHBOARD_METRIC_FORM });
    setErrors({});
    setMetricKeyTouched(false);
    setSuccessMessage(null);
    setEditorOpen(true);
  };

  const openEdit = (metric) => {
    createState.reset?.();
    updateState.reset?.();
    setSelectedMetric(metric);
    setForm(dashboardMetricToForm(metric));
    setErrors({});
    setMetricKeyTouched(true);
    setSuccessMessage(null);
    setEditorOpen(true);
  };

  const setFormValue = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleLabelChange = (label) => {
    setForm((current) => ({
      ...current,
      label,
      ...(!selectedMetric && !metricKeyTouched
        ? { metricKey: createDashboardMetricKey(label) }
        : {})
    }));
    setErrors((current) => {
      const next = { ...current };
      delete next.label;
      if (!selectedMetric && !metricKeyTouched) delete next.metricKey;
      return next;
    });
  };

  const handleMetricKeyChange = (metricKey) => {
    setMetricKeyTouched(true);
    setFormValue('metricKey', metricKey.toLowerCase());
  };

  const handleValueTypeChange = (valueType) => {
    setForm((current) => changeDashboardMetricValueType(current, valueType));
    setErrors((current) => {
      const next = { ...current };
      delete next.valueType;
      delete next.unitLabel;
      delete next.decimalPlaces;
      delete next.aggregation;
      return next;
    });
  };

  const submitMetric = async (event) => {
    event.preventDefault();
    const validation = validateDashboardMetric(form);
    setErrors(validation.errors);
    if (!validation.isValid) return;

    try {
      if (selectedMetric) {
        await updateMetric({
          dashboardTemplateId,
          metricDefinitionId: selectedMetric.metricDefinitionId,
          ...validation.values
        }).unwrap();
        setSuccessMessage(`${validation.values.label} updated.`);
      } else {
        await createMetric({
          dashboardTemplateId,
          ...validation.values
        }).unwrap();
        setSuccessMessage(`${validation.values.label} added.`);
      }
      setEditorOpen(false);
    } catch (_error) {
      // RTK Query exposes the normalized error through the mutation state.
    }
  };

  const moveMetric = async (metricDefinitionId, direction) => {
    clearActionStates();
    try {
      await reorderMetrics({
        dashboardTemplateId,
        metricDefinitionIds: moveDashboardMetric(
          metrics,
          metricDefinitionId,
          direction
        )
      }).unwrap();
      setSuccessMessage('Metric order updated.');
    } catch (_error) {
      // RTK Query exposes the normalized error through reorderState.
    }
  };

  const confirmArchive = async () => {
    if (!archiveMetric) return;
    clearActionStates();
    try {
      await archiveDashboardMetric({
        dashboardTemplateId,
        metricDefinitionId: archiveMetric.metricDefinitionId
      }).unwrap();
      setSuccessMessage(`${archiveMetric.label} archived.`);
      setArchiveMetric(null);
    } catch (_error) {
      // Keep the dialog open so the API error remains visible.
    }
  };

  const restoreMetric = async (metric) => {
    clearActionStates();
    try {
      await restoreDashboardMetric({
        dashboardTemplateId,
        metricDefinitionId: metric.metricDefinitionId
      }).unwrap();
      setSuccessMessage(`${metric.label} restored.`);
    } catch (_error) {
      // RTK Query exposes the normalized error through restoreState.
    }
  };

  return (
    <Box data-testid="dashboard-metric-designer" sx={{ mt: 4 }}>
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography component="h2" fontSize={22} fontWeight={700}>
            Dashboard metrics
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            Configure data entry, approval, aggregation, ordering, and display
            metadata.
          </Typography>
        </Box>
        {canEdit && (
          <Button onClick={openCreate} startIcon={<Add />} variant="contained">
            Add metric
          </Button>
        )}
      </Stack>

      <Typography color="text.secondary" sx={{ mt: 1.5 }} variant="body2">
        {
          metrics.filter(
            (metric) =>
              metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
          ).length
        }{' '}
        active ·{' '}
        {
          metrics.filter(
            (metric) =>
              metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ARCHIVED
          ).length
        }{' '}
        archived
      </Typography>

      {!canEdit && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You can view this metric configuration, but you do not have permission
          to change it.
        </Alert>
      )}
      {successMessage && (
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ mt: 2 }}
        >
          {successMessage}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {getDashboardErrorMessage(actionError)}
        </Alert>
      )}

      {metrics.length ? (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {metrics.map((metric, index) => (
            <MetricRow
              canEdit={canEdit}
              disableMoveDown={index === metrics.length - 1}
              disableMoveUp={index === 0}
              isBusy={isBusy}
              key={metric.metricDefinitionId}
              metric={metric}
              onArchive={() => {
                clearActionStates();
                setArchiveMetric(metric);
              }}
              onEdit={() => openEdit(metric)}
              onMoveDown={() => moveMetric(metric.metricDefinitionId, 1)}
              onMoveUp={() => moveMetric(metric.metricDefinitionId, -1)}
              onRestore={() => restoreMetric(metric)}
            />
          ))}
        </Stack>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            mt: 2,
            p: 3,
            textAlign: 'center'
          }}
        >
          <Typography fontWeight={700}>No metrics in this draft</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            Add the first metric to define what customers can submit and view.
          </Typography>
          {canEdit && (
            <Button onClick={openCreate} sx={{ mt: 2 }} variant="outlined">
              Add first metric
            </Button>
          )}
        </Paper>
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        onClose={() => !isBusy && setEditorOpen(false)}
        open={editorOpen}
      >
        <Box component="form" noValidate onSubmit={submitMetric}>
          <DialogTitle>
            {selectedMetric ? 'Edit metric' : 'Add metric'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              {selectedMetric && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Once this metric has recorded data, its key, value type, unit,
                  and decimal precision can no longer be changed. Labels,
                  approval, limits, and colour remain editable.
                </Alert>
              )}
              <DashboardFieldSourceSelector
                source={form.fieldSource}
                disabled={!!selectedMetric}
                onChange={(source, config) =>
                  setForm((current) => ({
                    ...current,
                    fieldSource: source,
                    ...(config ? {
                      valueType: config.format === 'percentage' ? 'percentage' : 'decimal',
                      unitLabel: config.format === 'percentage' ? '%' : config.currency || config.weight_unit || config.distance_unit || '',
                      decimalPlaces: String(config.decimal_places ?? 0), minimum: config.min ?? '', maximum: config.max ?? '',
                      ...(config.format === 'percentage' && current.aggregation === 'sum' ? { aggregation: 'average' } : {})
                    } : {}),
                    ...(source
                      ? { isRequired: false, approvalMode: 'not_required' }
                      : {})
                  }))
                }
              />
              {form.fieldSource && <Alert severity="info" sx={{ my: 2 }}>Value type, units, decimal places and limits are inherited from the linked field and follow its configuration.</Alert>}
              {errors.fieldSource && (
                <Alert severity="error">{errors.fieldSource}</Alert>
              )}
              <MetricFields
                errors={errors}
                form={form}
                onChange={setFormValue}
                onLabelChange={handleLabelChange}
                onMetricKeyChange={handleMetricKeyChange}
                onValueTypeChange={handleValueTypeChange}
              />
              {(createState.error || updateState.error) && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {getDashboardErrorMessage(
                    createState.error || updateState.error
                  )}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button disabled={isBusy} onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button disabled={isBusy} type="submit" variant="contained">
              {isBusy
                ? 'Saving…'
                : selectedMetric
                  ? 'Save metric'
                  : 'Add metric'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        maxWidth="sm"
        onClose={() => !archiveState.isLoading && setArchiveMetric(null)}
        open={Boolean(archiveMetric)}
      >
        <DialogTitle>Archive {archiveMetric?.label}?</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Archived data is retained and can be restored. This metric will stop
            accepting new values and will be removed from every graph and widget
            in the current draft.
          </Alert>
          {archiveState.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {getDashboardErrorMessage(archiveState.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={archiveState.isLoading}
            onClick={() => setArchiveMetric(null)}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            disabled={archiveState.isLoading}
            onClick={confirmArchive}
            variant="contained"
          >
            {archiveState.isLoading ? 'Archiving…' : 'Archive metric'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardMetricDesigner;
